import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './RelayRunnersPopup.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import SearchSelectAthlete from 'components/common/SearchSelectAthlete';
import {
  runnerChoicesForLeg,
  runnerLegs,
  runnersRequest,
} from 'utils/relayTeams';

// A relay team's runners (utils/relayTeams.js; step 3 file, section 13): one
// search box a leg, Leg 1 to the relay's last, each of the meet's athletes.
// A leg may be left empty. Its own dialog, not the confirm dialog: that one
// scrolls its content, which would cut off a leg's list of athletes.
const RelayRunnersPopup = ({
  isOpen,
  teamName,
  legCount,
  runners,
  athletes,
  groupingBasis,
  isSaving,
  onSave,
  onClose,
}) => {
  const [legs, setLegs] = useState([]);
  const [openLeg, setOpenLeg] = useState(null);

  // The team's legs when the dialog opens; forgotten when it closes, so the
  // next team's dialog never starts with this one's names.
  useEffect(() => {
    setLegs(isOpen ? runnerLegs(runners, legCount) : []);
    setOpenLeg(null);
  }, [isOpen, runners, legCount]);

  if (!isOpen) return null;

  const setLegAthlete = (leg, athlete) =>
    setLegs((prev) =>
      prev.map((l) =>
        l.leg !== leg
          ? l
          : athlete
            ? {
                leg,
                athleteID: athlete.athleteID,
                name: `${athlete.firstName} ${athlete.lastName}`,
              }
            : { leg, athleteID: '', name: '' }
      )
    );

  return (
    // Closes only by its buttons and the ×, never by a click outside.
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Runners: {teamName}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.description}>
            One athlete a leg, in the order they ran. A leg may be left
            empty. The runners are shown with the team; they do not change its
            place, or anyone&apos;s points or bests.
          </div>
          {legs.map((l) => (
            <div key={l.leg} className={styles.legRow}>
              <div className={styles.legLabel}>Leg {l.leg}</div>
              <div className={styles.legSearch}>
                <SearchSelectAthlete
                  columnKey={`leg${l.leg}`}
                  row={{ athleteName: l.name }}
                  options={runnerChoicesForLeg(athletes, legs, l.leg)}
                  showSelect={openLeg === l.leg}
                  handleShowSelect={(key, show) =>
                    setOpenLeg(show ? l.leg : null)
                  }
                  handleOptionChange={(option) =>
                    setLegAthlete(l.leg, option || null)
                  }
                  handleDeleteIconClick={() => setLegAthlete(l.leg, null)}
                  groupingBasis={groupingBasis}
                />
              </div>
            </div>
          ))}
          <div className={styles.actions}>
            <CustomNewButton
              label="Cancel"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            />
            <CustomNewButton
              label={isSaving ? 'Saving...' : 'Save'}
              variant="primary"
              onClick={() => onSave(runnersRequest(legs))}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

RelayRunnersPopup.propTypes = {
  isOpen: PropTypes.bool,
  teamName: PropTypes.string,
  legCount: PropTypes.number,
  runners: PropTypes.arrayOf(PropTypes.object),
  athletes: PropTypes.arrayOf(PropTypes.object),
  groupingBasis: PropTypes.number,
  isSaving: PropTypes.bool,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

export default RelayRunnersPopup;
